
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DeviceAllocation {
  deviceId: string;
  deviceName?: string;
  allocatedCredits: number;
}

export const useDeviceAllocation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const saveDeviceAllocation = useMutation({
    mutationFn: async ({ 
      orderId, 
      allocations 
    }: {
      orderId: string;
      allocations: DeviceAllocation[];
    }) => {
      console.log("🎯 Starting simplified device allocation for order:", orderId);
      console.log("📋 Allocations:", allocations);

      // Step 1: Validate Order
      console.log("📝 Step 1: Validating order...");
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select('id, customer_user_id, organization_id')
        .eq('id', orderId)
        .maybeSingle();

      if (orderError) {
        console.error("❌ Order query failed:", orderError);
        throw new Error(`Failed to fetch order: ${orderError.message}`);
      }

      if (!order) {
        console.error("❌ Order not found");
        throw new Error("Order not found");
      }

      if (!order.customer_user_id) {
        console.error("❌ Order has no customer assigned");
        throw new Error("Order has no customer assigned. Cannot allocate devices.");
      }

      console.log("✅ Order validated. Customer ID:", order.customer_user_id, "Organization ID:", order.organization_id);

      // Step 2: Get all admin users in the same organization  
      console.log("📝 Step 2: Finding admin users in organization...");
      const adminUserIds = [order.customer_user_id]; // Always include the customer

      if (order.organization_id) {
        const { data: adminUsers, error: adminError } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('organization_id', order.organization_id)
          .eq('role', 'admin');

        if (adminError) {
          console.warn("⚠️ Failed to fetch admin users:", adminError);
        } else if (adminUsers && adminUsers.length > 0) {
          const additionalAdminIds = adminUsers
            .map(u => u.user_id)
            .filter(id => id !== order.customer_user_id); // Don't duplicate customer
          
          adminUserIds.push(...additionalAdminIds);
        }
      }
      
      console.log("👥 Using auth user IDs for device assignments:", adminUserIds);

      // Step 3: Create Device Assignments for all users
      console.log("📝 Step 3: Creating device assignments...");
      const deviceIds = allocations.map(a => a.deviceId);
      
      // Check existing assignments for all devices and users
      const { data: existingAssignments, error: checkError } = await supabase
        .from('device_assignments')
        .select('device_id, user_id')
        .in('device_id', deviceIds)
        .in('user_id', adminUserIds);

      if (checkError) {
        console.error("❌ Failed to check existing assignments:", checkError);
        throw new Error(`Failed to check device assignments: ${checkError.message}`);
      }

      // Create assignment combinations that don't exist yet
      const newAssignments = [];
      
      for (const userId of adminUserIds) {
        for (const deviceId of deviceIds) {
          const exists = existingAssignments?.some(
            assignment => assignment.device_id === deviceId && assignment.user_id === userId
          );
          
          if (!exists) {
            newAssignments.push({
              device_id: deviceId,
              user_id: userId,
              assigned_at: new Date().toISOString()
            });
          }
        }
      }

      if (newAssignments.length > 0) {
        console.log("📝 Inserting new device assignments:", newAssignments);
        
        const { error: assignmentError } = await supabase
          .from('device_assignments')
          .insert(newAssignments);

        if (assignmentError) {
          console.error("❌ Failed to create device assignments:", assignmentError);
          throw new Error(`Failed to assign devices: ${assignmentError.message}`);
        }

        console.log("✅ Created", newAssignments.length, "new device assignments for", adminUserIds.length, "users");
      } else {
        console.log("ℹ️ All devices already assigned to all relevant users");
      }

      // Step 4: Update Order
      console.log("📝 Step 4: Updating order with allocated devices...");
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          allocated_device_ids: deviceIds,
          devices_allocated_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (updateError) {
        console.error("❌ Failed to update order:", updateError);
        throw new Error(`Failed to update order: ${updateError.message}`);
      }

      console.log("✅ Order updated successfully");

      // Step 5: Call BuildShip API for device assignment
      console.log("📝 Step 5: Calling BuildShip API...");
      try {
        const buildshipAuth = await supabase.functions.invoke('get-buildship-token');
        
        if (buildshipAuth.data?.token) {
          for (const allocation of allocations) {
            await fetch('https://i56o7w.buildship.run/device-assignment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${buildshipAuth.data.token}`,
              },
              body: JSON.stringify({
                orderId: orderId,
                deviceId: allocation.deviceId,
                deviceName: allocation.deviceName || '',
                allocatedCredits: allocation.allocatedCredits,
                customerUserId: order.customer_user_id,
                organizationId: order.organization_id,
                assignedUserCount: adminUserIds.length
              }),
            });
          }
          console.log("✅ BuildShip API calls completed");
        }
      } catch (error) {
        console.warn("⚠️ BuildShip API call failed:", error);
        // Don't fail the entire operation if BuildShip API fails
      }

      console.log("🎉 Device allocation completed successfully");

      return { 
        orderId, 
        customerUserId: order.customer_user_id,
        allocatedDevices: deviceIds,
        assignedUserCount: adminUserIds.length,
        newAssignments: newAssignments.length,
        existingAssignments: (existingAssignments?.length || 0)
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['device-assignments'] });
      
      toast({
        title: "Success",
        description: `Successfully allocated ${data.allocatedDevices.length} devices to ${data.assignedUserCount} users (${data.newAssignments} new assignments, ${data.existingAssignments} existing)`,
      });
    },
    onError: (error) => {
      console.error("💥 Device allocation failed:", error);
      toast({
        title: "Allocation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    saveDeviceAllocation
  };
};
